import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { 
  Search, 
  User, 
  AlertTriangle,
  Users,
  UserPlus,
  X,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";

// Import refactored components
import {
  WorkerStats,
  WorkerCard,
  PendingWorkersSection,
  AllUsersTab,
  FormerWorkersTab,
  DeleteUserDialog,
  MessageDialog,
  DocumentPreviewDialog,
  WorkerDetailsDialog,
} from "@/components/admin/workers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWorkers() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerDialogOpen, setWorkerDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [formerWorkers, setFormerWorkers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("all"); // Default to All Users
  const [impersonating, setImpersonating] = useState(false);
  
  // Available roles (fetched from API)
  const [availableRoles, setAvailableRoles] = useState([]);
  
  // Add Worker Modal State
  const [addWorkerModalOpen, setAddWorkerModalOpen] = useState(false);
  const [newWorkerData, setNewWorkerData] = useState({
    name: "",
    email: "",
    password: "",
    role: "worker"
  });
  const [creatingWorker, setCreatingWorker] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchWorkers();
    fetchFormerWorkers();
    fetchAllUsers();
    fetchAvailableRoles();
  }, [statusFilter]);

  const fetchAvailableRoles = async () => {
    try {
      const response = await axios.get(`${API}/admin/roles`);
      setAvailableRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
      // Fallback to default roles if API fails
      setAvailableRoles([
        { role_id: "worker", name: "Worker" },
        { role_id: "team_leader", name: "Team Leader" }
      ]);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await axios.get(`${API}/workers`, {
        params: statusFilter ? { status: statusFilter } : {},
      });
      setWorkers(response.data);
    } catch (error) {
      console.error("Error fetching workers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormerWorkers = async () => {
    try {
      const response = await axios.get(`${API}/workers/former`);
      setFormerWorkers(response.data);
    } catch (error) {
      console.error("Error fetching former workers:", error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      // API returns {users: [...], total: ...} - extract the users array
      const usersData = response.data?.users || response.data || [];
      setAllUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error("Error fetching all users:", error);
      setAllUsers([]);
    }
  };

  const handleApproveWorker = async (userId) => {
    try {
      await axios.put(`${API}/workers/${userId}/approve`);
      toast.success(t('success.updated'));
      fetchWorkers();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleVerifyNorwegianId = async (userId, verified) => {
    try {
      await axios.put(`${API}/workers/${userId}/verify-id?verified=${verified}`);
      toast.success(t('success.updated'));
      fetchWorkers();
      if (selectedWorker?.user_id === userId) {
        const updated = workers.find(w => w.user_id === userId);
        if (updated?.profile) {
          setSelectedWorker({
            ...selectedWorker,
            profile: { ...updated.profile, norwegian_id_verified: verified }
          });
        }
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleVerifyDocument = async (userId, documentId, verified) => {
    try {
      await axios.put(`${API}/workers/${userId}/documents/${documentId}/verify?verified=${verified}`);
      toast.success(t('success.updated'));
      fetchWorkers();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleUpdateHmsStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/workers/${userId}/hms-status?status=${status}`);
      toast.success(t('success.updated'));
      fetchWorkers();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/workers/${userId}/role?new_role=${newRole}`);
      toast.success(t('success.updated'));
      fetchWorkers();
      fetchFormerWorkers();
      fetchAllUsers();
      if (selectedWorker?.user_id === userId) {
        setSelectedWorker({ ...selectedWorker, role: newRole });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const restoreWorkerRole = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { new_role: "worker" });
      toast.success(t('success.updated'));
      fetchWorkers();
      fetchFormerWorkers();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore worker role");
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { new_role: newRole });
      toast.success(`Role changed to ${newRole.replace('_', ' ')}`);
      fetchWorkers();
      fetchFormerWorkers();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change role");
    }
  };

  const openWorkerDetails = (worker) => {
    setSelectedWorker(worker);
    setWorkerDialogOpen(true);
  };

  const openDeleteDialog = (worker) => {
    setWorkerToDelete(worker);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!workerToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${workerToDelete.user_id}`);
      toast.success(`User ${workerToDelete.name} deleted successfully`);
      setDeleteDialogOpen(false);
      setWorkerToDelete(null);
      if (selectedWorker?.user_id === workerToDelete.user_id) {
        setWorkerDialogOpen(false);
        setSelectedWorker(null);
      }
      fetchWorkers();
      fetchFormerWorkers();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedWorker) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/admin/send-message`, {
        user_id: selectedWorker.user_id,
        message: messageText.trim()
      });
      toast.success(t('success.sent'));
      setMessageDialogOpen(false);
      setMessageText("");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setSendingMessage(false);
    }
  };

  const openDocumentPreview = (doc) => {
    setSelectedDocument(doc);
    setDocumentPreviewOpen(true);
  };

  const openMessageDialog = (worker) => {
    setSelectedWorker(worker);
    setMessageDialogOpen(true);
  };

  // Impersonate user function
  const handleImpersonate = async (targetUserId) => {
    setImpersonating(true);
    try {
      const res = await axios.post(`${API}/admin/impersonate/${targetUserId}`);
      
      // Save impersonation info to localStorage
      localStorage.setItem('impersonation_info', JSON.stringify(res.data.impersonated_by));
      localStorage.setItem('scaffoldx_token', res.data.token);  // Use correct token key
      
      // Update user in context
      if (setUser) {
        setUser(res.data.user);
      }
      
      toast.success(t('success.updated'));
      
      // Navigate to appropriate dashboard
      const roleRoutes = {
        platform_admin: '/admin',
        operations_manager: '/admin',
        client_admin: '/client',
        client_viewer: '/client',
        team_leader: '/team-leader',
        worker: '/worker',
        webshop_admin: '/admin/webshop',
        rentals_admin: '/admin/rentals'
      };
      
      // Reload to apply new session
      window.location.href = roleRoutes[res.data.user.role] || '/';
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setImpersonating(false);
    }
  };

  const pendingWorkers = workers.filter(w => !w.is_approved);

  // Create new worker function
  const handleCreateWorker = async () => {
    if (!newWorkerData.name.trim() || !newWorkerData.email.trim() || !newWorkerData.password.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }
    
    if (newWorkerData.password.length < 6) {
      toast.error(t('errors.weakPassword'));
      return;
    }
    
    setCreatingWorker(true);
    try {
      await axios.post(`${API}/admin/create-worker`, {
        name: newWorkerData.name.trim(),
        email: newWorkerData.email.trim().toLowerCase(),
        password: newWorkerData.password,
        role: newWorkerData.role
      });
      
      toast.success(t('success.created'));
      setAddWorkerModalOpen(false);
      setNewWorkerData({ name: "", email: "", password: "", role: "worker" });
      fetchWorkers();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setCreatingWorker(false);
    }
  };

  const filteredWorkers = workers.filter(
    (worker) =>
      worker.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      worker.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      worker.profile?.worker_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout navItems={adminNavItems} title={t('nav.workers')}>
      <div className="space-y-6" data-testid="admin-workers">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1 w-full sm:max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
              <Input
                type="text"
                placeholder={t('workers.searchWorkers')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setAddWorkerModalOpen(true)}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-semibold"
              data-testid="add-worker-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {t('workers.hireWorker')}
            </Button>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD] focus:outline-none"
            >
            <option value="">{t('workers.allWorkers')}</option>
            <option value="pending">{t('workers.pendingWorkers')}</option>
            <option value="approved">{t('common.approved')}</option>
          </select>
          </div>
        </div>

        {/* Stats */}
        <WorkerStats workers={workers} allUsers={allUsers} />

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
          <Button
            variant={activeTab === "current" ? "default" : "ghost"}
            onClick={() => setActiveTab("current")}
            className={activeTab === "current" ? "bg-[#23F7DD] text-[#050505]" : "text-[#94A3B8] hover:text-white"}
          >
            <User className="w-4 h-4 mr-2" />
            {t('nav.workers')} ({workers.length})
          </Button>
          <Button
            variant={activeTab === "former" ? "default" : "ghost"}
            onClick={() => setActiveTab("former")}
            className={activeTab === "former" ? "bg-yellow-500 text-black" : "text-[#94A3B8] hover:text-white"}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            {t('admin.formerWorkers')} ({formerWorkers.length})
          </Button>
          <Button
            variant={activeTab === "all" ? "default" : "ghost"}
            onClick={() => setActiveTab("all")}
            className={activeTab === "all" ? "bg-blue-500 text-white" : "text-[#94A3B8] hover:text-white"}
          >
            <Users className="w-4 h-4 mr-2" />
            {t('admin.allUsers')} ({allUsers.length})
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === "current" && (
          <>
            <PendingWorkersSection
              pendingWorkers={pendingWorkers}
              onApprove={handleApproveWorker}
              onSendMessage={openMessageDialog}
            />

            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : filteredWorkers.length === 0 ? (
                  <div className="text-center py-12">
                    <User className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                    <p className="text-[#94A3B8]">{t('admin.noWorkersFound')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {filteredWorkers.map((worker) => (
                      <WorkerCard
                        key={worker.user_id}
                        worker={worker}
                        onApprove={handleApproveWorker}
                        onViewDetails={openWorkerDetails}
                        onDelete={openDeleteDialog}
                        onSendMessage={openMessageDialog}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === "former" && (
          <FormerWorkersTab
            formerWorkers={formerWorkers}
            onRestoreRole={restoreWorkerRole}
            onViewDetails={openWorkerDetails}
          />
        )}

        {activeTab === "all" && (
          <AllUsersTab
            allUsers={allUsers}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onChangeRole={changeUserRole}
            onViewDetails={openWorkerDetails}
            onDelete={openDeleteDialog}
            onImpersonate={handleImpersonate}
            currentUserId={user?.user_id}
          />
        )}

        {/* Dialogs */}
        <DeleteUserDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          user={workerToDelete}
          onDelete={handleDeleteUser}
          deleting={deleting}
        />

        <WorkerDetailsDialog
          open={workerDialogOpen}
          onOpenChange={setWorkerDialogOpen}
          worker={selectedWorker}
          onVerifyId={handleVerifyNorwegianId}
          onVerifyDocument={handleVerifyDocument}
          onUpdateHmsStatus={handleUpdateHmsStatus}
          onChangeRole={handleChangeRole}
          onSendMessage={openMessageDialog}
          onDelete={openDeleteDialog}
          onDocumentPreview={openDocumentPreview}
          onRefresh={fetchWorkers}
        />

        <MessageDialog
          open={messageDialogOpen}
          onOpenChange={setMessageDialogOpen}
          recipient={selectedWorker}
          messageText={messageText}
          setMessageText={setMessageText}
          onSend={handleSendMessage}
          sending={sendingMessage}
        />

        <DocumentPreviewDialog
          open={documentPreviewOpen}
          onOpenChange={setDocumentPreviewOpen}
          document={selectedDocument}
        />

        {/* Add Worker Modal */}
        {addWorkerModalOpen && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-[#0F0F0F] border-white/10 w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#23F7DD]/10 rounded-lg flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-[#23F7DD]" />
                    </div>
                    <h2 className="text-xl font-bold text-white">{t('admin.addWorker')}</h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setAddWorkerModalOpen(false);
                      setNewWorkerData({ name: "", email: "", password: "", role: "worker" });
                    }}
                    className="text-[#94A3B8] hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-white mb-2 block">{t('admin.fullName')} *</Label>
                    <Input
                      placeholder={t('admin.enterWorkerName')}
                      value={newWorkerData.name}
                      onChange={(e) => setNewWorkerData({ ...newWorkerData, name: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">{t('admin.email')} *</Label>
                    <Input
                      type="email"
                      placeholder="worker@example.com"
                      value={newWorkerData.email}
                      onChange={(e) => setNewWorkerData({ ...newWorkerData, email: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">{t('admin.password')} *</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder={t('admin.minChars')}
                        value={newWorkerData.password}
                        onChange={(e) => setNewWorkerData({ ...newWorkerData, password: e.target.value })}
                        className="bg-white/5 border-white/10 text-white pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-[#94A3B8] hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">{t('admin.role')}</Label>
                    <select
                      value={newWorkerData.role}
                      onChange={(e) => setNewWorkerData({ ...newWorkerData, role: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 rounded-lg focus:border-[#23F7DD] focus:outline-none"
                    >
                      {availableRoles.length > 0 ? (
                        availableRoles.map((role) => (
                          <option key={role.role_id} value={role.role_id}>
                            {role.name}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="worker">{t('admin.worker')}</option>
                          <option value="team_leader">{t('admin.teamLeader')}</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-400">
                      {t('admin.workerCredentialsInfo')}
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setAddWorkerModalOpen(false);
                        setNewWorkerData({ name: "", email: "", password: "", role: "worker" });
                      }}
                      className="flex-1 border-white/20 text-white hover:bg-white/10"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleCreateWorker}
                      disabled={creatingWorker || !newWorkerData.name || !newWorkerData.email || !newWorkerData.password}
                      className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] disabled:opacity-50"
                      data-testid="create-worker-btn"
                    >
                      {creatingWorker ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('admin.creating')}
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-2" />
                          {t('admin.createWorker')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
